import React, { useState, useEffect, useCallback } from "react";
import { IoMdClose } from "react-icons/io";
import { IoCopyOutline, IoShareOutline, IoCheckmarkCircle } from "react-icons/io5";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { logError } from "../Utilities/errorLogger";
import { generateTeamUrl, copyToClipboard, shareLink } from "../Utilities/shareUtils";

const InviteToPool = ({ poolId, onAddEmail, onSendInvites, isSubmitting, onClose }) => {
  const [entries, setEntries] = useState([{ email: "", name: "" }]);
  const [sentResults, setSentResults] = useState([]);
  const [hasSent, setHasSent] = useState(false);
  const loggedInUserEmail = localStorage.getItem("email");

  const validateEntries = useCallback(
    (entryList) => {
      const validEntries = entryList.filter((e) => e.email.trim() !== "");

      if (validEntries.length === 0) {
        throw new Error("Please enter at least one email address");
      }

      if (validEntries.some((e) => e.email.toLowerCase() === loggedInUserEmail?.toLowerCase())) {
        throw new Error("You cannot invite yourself to the pool");
      }

      return validEntries;
    },
    [loggedInUserEmail]
  );

  const processInvite = useCallback(
    async (entry, currentDate) => {
      try {
        const response = await fetch("/api/email/invitePlayerEmail", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: entry.email,
            name: entry.name,
            poolId,
            currentDate,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.details || data.error || `Failed to invite ${entry.email}`);
        }

        return { email: entry.email, name: entry.name, magicToken: data.magicToken, success: true };
      } catch (error) {
        await logError({
          filePath: "Pool/InviteToPool.js",
          functionName: "processInvite",
          errorType: "Invite Error",
          errorMessage: error.message,
          stackTrace: error.stack,
          requestData: { email: entry.email, poolId },
        });
        return { email: entry.email, name: entry.name, success: false, error: error.message };
      }
    },
    [poolId]
  );

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return false;

    try {
      const validEntries = validateEntries(entries);
      const currentDate = moment().tz("America/Chicago").utc().format();

      const results = [];
      for (const entry of validEntries) {
        const result = await processInvite(entry, currentDate);
        results.push(result);
      }

      setSentResults(results);
      setHasSent(true);

      const successCount = results.filter((r) => r.success).length;
      const failCount = results.filter((r) => !r.success).length;

      if (successCount > 0 && failCount === 0) {
        toast.success(`${successCount} invite${successCount > 1 ? "s" : ""} sent!`, { theme: "dark" });
      } else if (successCount > 0) {
        toast.warning(`${successCount} sent, ${failCount} failed`, { theme: "dark" });
      } else {
        toast.error("Failed to send invites", { theme: "dark" });
      }

      return true;
    } catch (error) {
      await logError({
        filePath: "Pool/InviteToPool.js",
        functionName: "handleSubmit",
        errorType: "Invite Submit Error",
        errorMessage: error.message,
        stackTrace: error.stack,
        requestData: { entries, poolId },
      });
      toast.error(error.message || "Failed to send invitations", { theme: "dark" });
      return false;
    }
  }, [entries, validateEntries, processInvite, poolId, isSubmitting]);

  const handleCopyLink = async (magicToken) => {
    const url = generateTeamUrl(magicToken);
    const success = await copyToClipboard(url);
    if (success) {
      toast.success("Link copied!", { theme: "dark", autoClose: 2000 });
    }
  };

  const handleShare = async (magicToken, name) => {
    const url = generateTeamUrl(magicToken);
    await shareLink(url, "GolfPools Team", `Pick your team for the pool: ${url}`);
  };

  const handleEntryChange = (index, field, value) => {
    const newEntries = [...entries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    setEntries(newEntries);
  };

  const addEntryField = useCallback(() => {
    setEntries((prev) => [...prev, { email: "", name: "" }]);
  }, []);

  const removeEntryField = (index) => {
    if (entries.length > 1) {
      setEntries(entries.filter((_, i) => i !== index));
    }
  };

  useEffect(() => {
    if (onAddEmail) onAddEmail(addEntryField);
  }, [onAddEmail, addEntryField]);

  useEffect(() => {
    if (onSendInvites) onSendInvites(handleSubmit);
  }, [onSendInvites, handleSubmit]);

  // Show results after sending
  if (hasSent && sentResults.length > 0) {
    return (
      <div className="invite-form">
        <div className="invite-content">
          <div style={{ padding: "8px 0" }}>
            {sentResults.map((result, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 0",
                  borderBottom: index < sentResults.length - 1 ? "1px solid #e5e7eb" : "none",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: "#1a472a" }}>{result.name || result.email}</div>
                  {result.name && <div style={{ fontSize: "0.85rem", color: "#6b7280" }}>{result.email}</div>}
                  {!result.success && <div style={{ fontSize: "0.85rem", color: "#dc2626" }}>{result.error}</div>}
                </div>
                {result.success && result.magicToken && (
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => handleCopyLink(result.magicToken)}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        background: "white",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "0.85rem",
                      }}
                      title="Copy team link"
                    >
                      <IoCopyOutline /> Copy Link
                    </button>
                    <button
                      onClick={() => handleShare(result.magicToken, result.name)}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        background: "white",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "0.85rem",
                      }}
                      title="Share team link"
                    >
                      <IoShareOutline /> Share
                    </button>
                  </div>
                )}
                {result.success && !result.magicToken && (
                  <IoCheckmarkCircle style={{ color: "#16a34a", fontSize: "1.5rem" }} />
                )}
              </div>
            ))}
          </div>
          <button
            onClick={() => { setHasSent(false); setSentResults([]); setEntries([{ email: "", name: "" }]); }}
            style={{
              marginTop: "16px",
              padding: "8px 16px",
              color: "#1a472a",
              border: "1px solid #1a472a",
              borderRadius: "8px",
              background: "white",
              cursor: "pointer",
              width: "100%",
              fontWeight: 600,
            }}
          >
            Invite More People
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-4">
      <div className="space-y-3">
        {entries.map((entry, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="text"
              value={entry.name}
              onChange={(e) => handleEntryChange(index, "name", e.target.value)}
              placeholder="Name"
              className="w-[35%] px-3 py-2.5 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm text-gray-900 placeholder-gray-400"
              disabled={isSubmitting}
            />
            <input
              type="email"
              value={entry.email}
              onChange={(e) => handleEntryChange(index, "email", e.target.value)}
              placeholder="Email address"
              className="flex-1 px-3 py-2.5 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm text-gray-900 placeholder-gray-400"
              required
              disabled={isSubmitting}
            />
            {entries.length > 1 && (
              <button
                type="button"
                onClick={() => removeEntryField(index)}
                className="p-2 text-gray-400 hover:text-red-500 rounded-lg transition-all"
                aria-label="Remove"
                disabled={isSubmitting}
              >
                <IoMdClose size={18} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default InviteToPool;
